import { portalFetch, portalApiErrorMessage } from '../lib/portalApi';

export type UploadedImage = {
  id: number;
  url: string;
  s3_key: string;
  mime_type: string;
  size_bytes: number;
  reused?: boolean;
};

export type UploadResult =
  | { ok: true; data: UploadedImage }
  | { ok: false; status: number; message: string };

const UPLOAD_PATH = '/api/admin/ev123/uploads/image';

export const ALLOWED_UPLOAD_MIME = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const DEFAULT_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export function isAllowedImageMime(mime: string): boolean {
  return (ALLOWED_UPLOAD_MIME as readonly string[]).includes(mime);
}

export async function uploadAdminImage(file: File): Promise<UploadResult> {
  if (!isAllowedImageMime(file.type)) {
    return {
      ok: false,
      status: 415,
      message: '지원하지 않는 형식입니다. (jpg/png/webp만 허용)',
    };
  }
  if (file.size > DEFAULT_MAX_UPLOAD_BYTES) {
    return {
      ok: false,
      status: 413,
      message: `파일이 너무 큽니다. 최대 ${Math.floor(DEFAULT_MAX_UPLOAD_BYTES / 1024 / 1024)}MB.`,
    };
  }
  const fd = new FormData();
  fd.append('file', file, file.name || 'image');

  let res: Response;
  try {
    res = await portalFetch(UPLOAD_PATH, { method: 'POST', auth: true, body: fd });
  } catch (e) {
    return { ok: false, status: 0, message: (e as Error).message || '업로드 요청 실패' };
  }
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    return {
      ok: false,
      status: res.status,
      message: text.trim().slice(0, 280) || '서버 응답을 해석할 수 없습니다.',
    };
  }
  if (!res.ok) {
    return { ok: false, status: res.status, message: portalApiErrorMessage(res.status, body) };
  }
  const d = body as Partial<UploadedImage>;
  if (!d || typeof d.id !== 'number' || typeof d.url !== 'string') {
    return { ok: false, status: res.status, message: '서버 응답 형식이 올바르지 않습니다.' };
  }
  return {
    ok: true,
    data: {
      id: d.id,
      url: d.url,
      s3_key: typeof d.s3_key === 'string' ? d.s3_key : '',
      mime_type: typeof d.mime_type === 'string' ? d.mime_type : '',
      size_bytes: typeof d.size_bytes === 'number' ? d.size_bytes : 0,
      reused: !!d.reused,
    },
  };
}
